import * as UI from "@iyk/music-ui"
import { AppLink } from "@iyk/react-router/client"
import * as React from "react"
import { AppConfig } from "../../../AppConfig.ts"
import { AppRoutes } from "../../../AppRoutes.ts"
import { TruncateText } from "../../../ui/components/TruncateText.tsx"
import { WorldAvatar } from "../../../worlds/ui/WorldAvatar.tsx"
import type { UiUnlock } from "../../UiUnlock.ts"
import { UnlockPolicyDialog } from "./UnlockPolicyDialog.tsx"

// Horizontal padding for the card's body
export const bodyPadding = { left: 60, right: 16 } as const

export const Container = ({ className, ...props }: React.ComponentProps<"div">) => (
  <div
    className={UI.cx("w-full relative flex flex-col gap-3 py-4 z-10 border-b border-grayA-3", className)}
    {...props}
  />
)

type HeaderProps = { world: UiUnlock.World; title: string; date: string }

export const Header = ({ world, title, date }: HeaderProps) => (
  <div className="px-4 shrink-0 flex flex-col">
    <div className="flex items-start gap-3">
      <AppLink to={AppRoutes.worlds.home(world.slug)} className="shrink-0 mt-0.5">
        <WorldAvatar url={world.avatarUrl} name={world.name} size="md" />
      </AppLink>

      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1.5">
          <AppLink to={AppRoutes.worlds.home(world.slug)} className="inline-flex">
            <span className="text-xs-departure">{world.name}</span>
          </AppLink>

          <span className="text-xs text-gray-10">{date}</span>
        </div>

        <p className="text-sm">{title}</p>
      </div>
    </div>
  </div>
)

export const Footer = ({ caption }: { caption?: string }) => {
  if (!caption) return null

  return (
    <div className="pr-4 shrink-0 flex flex-col gap-2" style={{ paddingLeft: bodyPadding.left }}>
      <TruncateText className="text-sm text-gray-11" lineClamp={2}>
        {caption}
      </TruncateText>
    </div>
  )
}

export function formatDate(date: Date, locale: string): string {
  return new Date(date).toLocaleDateString(locale, {
    month: "short",
    day: "numeric"
  })
}

type LockedUnlockProps = {
  id: string
  title: string
  caption?: string
  world: UiUnlock.World
  createdAt: Date
  policy: UiUnlock.Policy
  mediaCount?: number
  locale: string
}

export const LockedUnlock = ({
  id,
  title,
  caption,
  world,
  createdAt,
  policy,
  mediaCount,
  locale
}: LockedUnlockProps) => {
  const formattedCreatedAt = formatDate(createdAt, locale)

  return (
    <Container key={id}>
      <Header world={world} title={title} date={formattedCreatedAt} />
      <UnlockPolicyDialog.Root policy={policy} world={world}>
        <div className="flex" style={{ paddingLeft: bodyPadding.left, paddingRight: bodyPadding.right }}>
          <UnlockPolicyDialog.Trigger className="grow group flex flex-col gap-3">
            <span
              style={{ backgroundImage: `url(${AppConfig.assets.lockedThumbnailUrl})` }}
              className="grow w-full flex items-center justify-center bg-cover bg-center relative aspect-video rounded border border-grayA-3 bg-gray-1 cursor-pointer overflow-hidden"
            >
              {mediaCount !== undefined && <UI.ContentUnlockBadge mediaCount={mediaCount} />}
              <span
                className={UI.cx(
                  "group-hover:bg-gray-3 transition-colors duration-300",
                  "bg-gray-1 text-gray-12",
                  "rounded-2xl h-8 px-2.5",
                  "flex items-center justify-center gap-2"
                )}
              >
                <UI.Icon.Lock />

                <span className="text-xs">How to access</span>
              </span>
            </span>
          </UnlockPolicyDialog.Trigger>
        </div>
      </UnlockPolicyDialog.Root>
      <Footer caption={caption} />
    </Container>
  )
}
